import { IDirective, IDirectiveFactory, IQService, IScope } from "angular";
import { IModalService, IModalStackService } from "angular-ui-bootstrap";

import { IComment, CommentService } from "@gtmhub/comments";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { UserProfileService } from "@gtmhub/users/userProfile/profileService";
import { IProfile } from "@webapp/users/models/users.models";
import { IUser } from "@gtmhub/users/models";

interface INotifyUsersWithoutOwnership extends IScope {
  ownership: string;
  usersData;

  translatedOwnership: string;
  users: Partial<IUser>[];
  openNotifyModal(): void;
}

interface INotifyModal extends IScope {
  indicators: {
    posting: boolean;
  };

  translatedOwnership: string;
  currentUserProfile: IProfile;
  notifyText: string;
  postComment(): void;
  commentMentionsChanged(mentions: string[]): void;
}

export class NotifyUsersWithoutOwnership implements IDirective {
  public scope = {
    ownership: "@",
    usersData: "=",
  };
  public template = `<a class="link-accent" ng-click="openNotifyModal()" gh-track="Nudge Users Without Ownership" gh-track-meta="{ownership: '{{ownership}}'}">Nudge people without {{ownership}}</a>`;

  constructor(
    private $uibModal: IModalService,
    private $q: IQService,
    private commentService: CommentService,
    private userProfileService: UserProfileService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {}

  public link(scope: INotifyUsersWithoutOwnership): void {
    scope.users = scope.usersData && scope.usersData.default ? scope.usersData.default : [scope.usersData];
    scope.translatedOwnership = localize(scope.ownership);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    scope.openNotifyModal = () => {
      this.$uibModal.open({
        templateUrl: require("../views/notify-users-without-ownership.jade"),
        windowClass: "medium",
        controller: [
          "$scope",
          "$uibModalStack",
          function ($scope: INotifyModal, $uibModalStack: IModalStackService) {
            $scope.indicators = {
              posting: false,
            };
            $scope.translatedOwnership = scope.translatedOwnership;
            $scope.currentUserProfile = ctrl.userProfileService.getProfile();
            let mentions: string[] = [];

            $scope.commentMentionsChanged = (changedMentions: string[]): void => {
              mentions = changedMentions;
            };
            const baseText = localize("hi_is_there_anything_can_do_to_help_you_create_your");
            const targetTitle = `${localize("you_dont_own_any")} ${$scope.translatedOwnership}`;

            $scope.notifyText = `${baseText} ${$scope.translatedOwnership}?`;

            $scope.postComment = (): void => {
              $scope.indicators.posting = true;

              const commentsPromises = scope.users.map((user) => {
                const newComment: IComment = {
                  mentioned: [...mentions, user.id],
                  targetId: user.id,
                  targetType: "employee",
                  targetTitle,
                  text: $scope.notifyText,
                };

                return ctrl.commentService.post(newComment);
              });

              ctrl.$q.all(commentsPromises).then(
                () => {
                  $scope.indicators.posting = false;
                  $uibModalStack.dismissAll();
                },
                (error) => ctrl.uiErrorHandlingService.handleIndicator(error, { indicator: "posting" })
              );
            };
          },
        ],
      });
    };
  }

  public static factory(): IDirectiveFactory {
    const directive = (
      $uibModal: IModalService,
      $q: IQService,
      commentService: CommentService,
      userProfileService: UserProfileService,
      uiErrorHandlingService: UIErrorHandlingService
    ) => new NotifyUsersWithoutOwnership($uibModal, $q, commentService, userProfileService, uiErrorHandlingService);

    directive.$inject = ["$uibModal", "$q", "CommentService", "UserProfileService", "UIErrorHandlingService"];

    return directive;
  }
}
